import React, { useState } from "react";
import Reveal from "./Reveal";
import TestimonialFormModal from "./testimonials/TestimonialFormModal";
import { trackEvent } from "../../lib/track";

/**
 * What Are You Hearing In This?
 *
 * Reflection-enrollment section. Sits late in the homepage flow,
 * after Method / Work With Me / Proof. The intent is to mirror an
 * actual consultation: ask the visitor to locate themselves before
 * any transaction is offered.
 *
 * The CTA opens the existing TestimonialFormModal in REFLECTION
 * variant so the submission lands in the existing
 * /api/testimonials moderation queue. No backend / schema changes
 * required. The modal renders with reflection-first copy via the
 * `seed` prop so the form already knows it is collecting a
 * reflection, not a testimonial.
 */

const PROMPTS = [
  "What part of this feels true for where you are right now?",
  "Where does your momentum feel blocked?",
  "What are you trying to build, become, or finally move forward?",
  "What would clarity make possible for you?",
  "What kind of structure do you feel you need next?",
];

const WhatAreYouHearing = () => {
  const [open, setOpen] = useState(false);

  const openForm = () => {
    trackEvent("reflection_enrollment_open_clicked", { surface: "homepage" });
    setOpen(true);
  };

  return (
    <section
      id="what-are-you-hearing"
      data-testid="what-are-you-hearing-section"
      className="relative py-24 md:py-32 border-t border-gold/10"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
            Reflection
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-themed leading-tight tracking-tight font-light text-center max-w-3xl mx-auto">
            What are you hearing{" "}
            <span className="italic gold-gradient-text">in this?</span>
          </h2>
          <p className="mt-7 max-w-2xl mx-auto text-center text-white/85 leading-relaxed">
            If a line, idea, or pattern from this work stayed with you, share what it helped you see. The first step is not always buying something. Sometimes it is recognizing where momentum is blocked and what kind of support would help you move.
          </p>
        </Reveal>

        <Reveal delay={160}>
          <ul
            data-testid="reflection-prompts"
            className="mt-12 bg-white/5 border-t border-gold/20 p-7 md:p-10 rounded-lg border border-gold/10 space-y-4"
          >
            {PROMPTS.map((p, i) => (
              <li
                key={i}
                className="flex gap-4 text-white/85 text-[15.5px] md:text-[16px] leading-relaxed"
              >
                <span className="font-serif text-gold text-lg leading-none shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={260}>
          <div className="mt-12 text-center">
            <button
              type="button"
              data-testid="reflection-enrollment-cta"
              onClick={openForm}
              className="btn-gold"
            >
              Send Your Reflection
            </button>
            <p className="mt-5 text-[12px] tracking-[0.22em] uppercase text-gold/70">
              Your reflection may help someone else recognize their own.
            </p>
          </div>
        </Reveal>
      </div>

      <TestimonialFormModal
        open={open}
        onClose={() => setOpen(false)}
      />
    </section>
  );
};

export default WhatAreYouHearing;
